import React, { useEffect, useMemo, useState } from 'react'
import { Link, Route, Routes, useNavigate, useParams } from 'react-router-dom'
import { 
  Aperture, AlertCircle, ArrowRight, Copy, Download, Eye, 
  Lock, Plus, RefreshCw, Trash2, X 
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { toast } from 'sonner'
import type { 
  StatusThreat, Uncertainty, ObservationInput, Observation, 
  Profile, PublicErratum 
} from './lib/types'
import { THREAT_LABELS, THREAT_SHORT, UNCERTAINTY_LABELS } from './lib/types'

// ---------- API helpers (works with Pages Functions + direct Vite proxy in dev if needed) ----------

const API = '/api'

async function api<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(opts.headers || {}),
    },
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.error || `Request failed: ${res.status}`)
  }
  return res.json()
}

// ---------- Local admin key store (client-only, never sent to server except when you explicitly use it) ----------

const ADMIN_KEY = 'errata_admin_keys_v1'

function getAdminKeys(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(ADMIN_KEY) || '{}')
  } catch { return {} }
}
function saveAdminKey(slug: string, key: string) {
  const all = getAdminKeys()
  all[slug] = key
  localStorage.setItem(ADMIN_KEY, JSON.stringify(all))
}
function getAdminKey(slug: string): string | undefined {
  return getAdminKeys()[slug]
}
function removeAdminKey(slug: string) {
  const all = getAdminKeys()
  delete all[slug]
  localStorage.setItem(ADMIN_KEY, JSON.stringify(all))
}

// ---------- Turnstile (loaded on demand, test key works locally) ----------

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: any) => string
      reset: (id?: string) => void
      remove: (id: string) => void
    }
  }
}

let turnstileWidgetId: string | null = null

function loadTurnstile(): Promise<void> {
  return new Promise((resolve) => {
    if (window.turnstile) return resolve()
    const s = document.createElement('script')
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    s.async = true
    s.onload = () => resolve()
    document.head.appendChild(s)
  })
}

async function getTurnstileToken(container: HTMLDivElement, sitekey: string): Promise<string> {
  await loadTurnstile()
  return new Promise((resolve, reject) => {
    if (!window.turnstile) return reject(new Error('Turnstile failed to load'))

    if (turnstileWidgetId) {
      try { window.turnstile.reset(turnstileWidgetId) } catch {}
    } else {
      container.innerHTML = ''
      turnstileWidgetId = window.turnstile.render(container, {
        sitekey,
        theme: 'dark',
        callback: (token: string) => resolve(token),
        'error-callback': () => reject(new Error('Turnstile error')),
        'expired-callback': () => { /* will re-render on next submit */ },
      })
    }

    // Poll a bit in case callback is slow
    const start = Date.now()
    const check = setInterval(() => {
      const el = container.querySelector('input[name="cf-turnstile-response"]') as HTMLInputElement | null
      if (el?.value) {
        clearInterval(check)
        resolve(el.value)
      }
      if (Date.now() - start > 18000) {
        clearInterval(check)
        reject(new Error('Turnstile timed out — please retry'))
      }
    }, 180)
  })
}

// ---------- Beautiful Aperture Constellation Visualization (the heart of the profile) ----------

function ApertureConstellation({ observations }: { observations: Observation[] }) {
  const counts = useMemo(() => {
    const c: Record<StatusThreat, number> = { expertise:0, belonging:0, airtime:0, valence:0, repair:0, silence:0, other:0 }
    observations.forEach(o => { c[o.threat] = (c[o.threat] || 0) + 1 })
    return c
  }, [observations])

  const total = observations.length || 1
  const threats = (Object.keys(counts) as StatusThreat[]).filter(t => counts[t] > 0)

  // Fixed pleasant layout around a circle
  const centerX = 260
  const centerY = 168
  const radius = 108

  const positions = threats.map((t, i) => {
    const angle = (i / Math.max(threats.length, 1)) * Math.PI * 2 - Math.PI / 2
    return {
      threat: t,
      x: centerX + Math.cos(angle) * radius,
      y: centerY + Math.sin(angle) * radius * 0.82,
      size: Math.max(14, Math.min(42, (counts[t] / total) * 58 + 16)),
    }
  })

  const impactTotals = observations.reduce((acc, o) => {
    if (o.impacts.perception) acc.perception++
    if (o.impacts.choice) acc.choice++
    if (o.impacts.coordination) acc.coordination++
    return acc
  }, { perception: 0, choice: 0, coordination: 0 })

  return (
    <div className="constellation rounded-2xl border border-[#25283a] bg-[#0a0b12] p-5">
      <div className="flex items-center justify-between mb-3 px-1">
        <div>
          <div className="section-title tracking-[1.5px]">APERTURE CONSTELLATION</div>
          <div className="text-[13px] text-[#7f8496]">Status threats positioned by frequency • sized by volume</div>
        </div>
        <div className="text-right text-xs text-[#7f8496]">{observations.length} observations</div>
      </div>

      <svg viewBox="0 0 520 260" className="w-full h-auto" style={{ maxHeight: 268 }}>
        {/* faint rings = the instrument aperture */}
        <circle cx={centerX} cy={centerY} r={radius + 22} fill="none" stroke="#25283a" strokeWidth="1" />
        <circle cx={centerX} cy={centerY} r={radius - 8} fill="none" stroke="#1f2230" strokeWidth="18" />
        <circle cx={centerX} cy={centerY} r={radius} fill="none" stroke="#353a52" strokeWidth="1" />

        {/* subtle radial guides */}
        {Array.from({ length: 7 }).map((_, i) => {
          const a = (i / 7) * Math.PI * 2 - Math.PI / 2
          const x2 = centerX + Math.cos(a) * (radius + 18)
          const y2 = centerY + Math.sin(a) * (radius + 14) * 0.82
          return <line key={i} x1={centerX} y1={centerY} x2={x2} y2={y2} stroke="#1f2230" strokeWidth="1" />
        })}

        {/* connections to center (lost signal lines) */}
        {positions.map((p, idx) => (
          <line 
            key={idx}
            x1={centerX} y1={centerY} 
            x2={p.x} y2={p.y} 
            stroke="#c5a26f" 
            strokeOpacity={0.18 + (counts[p.threat] / total) * 0.35} 
            strokeWidth="1.5" 
          />
        ))}

        {/* the nodes */}
        {positions.map((p, idx) => {
          const count = counts[p.threat]
          const dominantU = observations.filter(o => o.threat === p.threat)
            .sort((a,b) => (b.uncertainty === 'evident' ? 1 : 0) - (a.uncertainty === 'evident' ? 1 : 0))[0]?.uncertainty || 'plausible'
          const color = dominantU === 'evident' ? '#c5a26f' : dominantU === 'plausible' ? '#8fa3c2' : '#5a5f75'
          return (
            <g key={idx}>
              <circle 
                cx={p.x} cy={p.y} r={p.size} 
                fill={color} 
                fillOpacity={0.9}
                stroke="#0a0b12" 
                strokeWidth="3"
              />
              <text 
                x={p.x} y={p.y + 4} 
                textAnchor="middle" 
                fontSize={p.size > 28 ? 12 : 10} 
                fill="#0a0b12" 
                fontWeight={600}
              >
                {count}
              </text>
              <text 
                x={p.x} y={p.y + p.size + 15} 
                textAnchor="middle" 
                fontSize="10" 
                fill="#c3c7d3"
              >
                {THREAT_SHORT[p.threat]}
              </text>
            </g>
          )
        })}

        {/* center aperture marker */}
        <circle cx={centerX} cy={centerY} r="7" fill="#c5a26f" />
        <circle cx={centerX} cy={centerY} r="3.5" fill="#0a0b12" />
      </svg>

      {/* Impact summary */}
      <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
        {([
          ['perception', 'Perception distorted', impactTotals.perception],
          ['choice', 'Choice affected', impactTotals.choice],
          ['coordination', 'Coordination affected', impactTotals.coordination],
        ] as const).map(([key, label, n]) => (
          <div key={key} className="rounded-xl border border-[#25283a] bg-[#11131c] px-4 py-3">
            <div className="text-[#7f8496] text-xs tracking-widest mb-1">{label.toUpperCase()}</div>
            <div className="text-3xl font-semibold tabular-nums text-[#f1f3f8]">{n}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ---------- Main App ----------

export default function App() {
  const [isAdminFor, setIsAdminFor] = useState<Record<string, boolean>>({})

  // Check which slugs we have admin keys for (purely client)
  useEffect(() => {
    const keys = getAdminKeys()
    const map: Record<string, boolean> = {}
    Object.keys(keys).forEach(s => { map[s] = true })
    setIsAdminFor(map)
  }, [])

  return (
    <div className="min-h-screen bg-[#0a0b12] text-[#c3c7d3]">
      {/* Observatory instrument header */}
      <header className="obs-header sticky top-0 z-50 border-b border-[#25283a]">
        <div className="container flex items-center justify-between py-4">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="aperture">
              <Aperture className="w-8 h-8 text-[#c5a26f]" />
            </div>
            <div>
              <div className="font-semibold tracking-[-0.3px] text-xl text-[#f1f3f8]">errata</div>
              <div className="text-[10px] text-[#7f8496] -mt-1">PUBLIC OBSERVATORY</div>
            </div>
          </Link>

          <div className="flex items-center gap-3 text-sm">
            <Link to="/public" className="btn btn-ghost hidden sm:flex items-center gap-2">
              <Eye className="w-4 h-4" /> Published Errata
            </Link>
            <Link to="/" className="btn btn-ghost flex items-center gap-2">
              <Plus className="w-4 h-4" /> New Observatory
            </Link>
          </div>
        </div>
      </header>

      <Routes>
        <Route path="/" element={<Home isAdminFor={isAdminFor} />} />
        <Route path="/e/:slug" element={<EventPage isAdminFor={isAdminFor} setIsAdminFor={setIsAdminFor} />} />
        <Route path="/public" element={<PublicGallery />} />
        <Route path="*" element={<NotFound />} />
      </Routes>

      {/* Prominent attribution + ethical footer */}
      <footer className="border-t border-[#25283a] mt-16 py-9 text-sm">
        <div className="container">
          <div className="flex flex-col md:flex-row md:items-center gap-y-4 justify-between text-[#7f8496]">
            <div className="max-w-prose">
              Errata exists to help organizers recover what the room could not sense. 
              It is not a social network. All contributions are anonymous. 
              Nothing is published without explicit organizer action and minimum viable observations.
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-2 items-center text-xs">
              <a 
                href="https://www.linkedin.com/in/mindful-abhay/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-[#c5a26f] flex items-center gap-1.5"
              >
                LinkedIn <ArrowRight className="w-3 h-3" />
              </a>
              <a 
                href="https://buymeacoffee.com/abhaypratapsingh" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-[#c5a26f] flex items-center gap-1.5"
              >
                Buy Me a Coffee <ArrowRight className="w-3 h-3" />
              </a>
              <span className="hidden md:inline text-[#353a52]">•</span>
              <span className="text-[11px]">Built as a public instrument • No accounts • No feeds • No performance</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

// ---------- Home / Landing + Create ----------

function Home({ isAdminFor }: { isAdminFor: Record<string, boolean> }) {
  const [creating, setCreating] = useState(false)
  const [adminKeyModal, setAdminKeyModal] = useState<{ slug: string; key: string } | null>(null)
  const navigate = useNavigate()

  const [form, setForm] = useState({
    title: '',
    context: '',
    closesInDays: '14',
    allowPublic: true,
    minObs: '3',
  })
  const [tsContainer, setTsContainer] = useState<HTMLDivElement | null>(null)

  const myObservatories = Object.keys(isAdminFor)

  async function createObservatory(e: React.FormEvent) {
    e.preventDefault()
    if (!form.title.trim()) return toast.error('Title is required')

    setCreating(true)
    try {
      // Get fresh Turnstile token
      if (!tsContainer) throw new Error('Verification widget not ready')
      const token = await getTurnstileToken(tsContainer, '1x00000000000000000000AA') // test key works locally + in prod you replace via env

      const res = await api<{ id: string; slug: string; adminKey: string; title: string }>('/events', {
        method: 'POST',
        body: JSON.stringify({
          title: form.title,
          context: form.context,
          closesInDays: parseInt(form.closesInDays),
          allowPublic: form.allowPublic,
          minObs: parseInt(form.minObs),
          turnstileToken: token,
        }),
      })

      // Store the one-time admin key locally only
      saveAdminKey(res.slug, res.adminKey)
      setAdminKeyModal({ slug: res.slug, key: res.adminKey })

      toast.success('Observatory created. Save your admin key now.')
      // Navigate to it
      setTimeout(() => navigate(`/e/${res.slug}`), 420)
    } catch (err: any) {
      toast.error(err.message || 'Failed to create observatory')
    } finally {
      setCreating(false)
    }
  }

  return (
    <div className="container pt-10 pb-20">
      {/* Hero instrument panel */}
      <div className="max-w-3xl">
        <div className="inline-flex items-center gap-2 text-xs tracking-[2px] text-[#c5a26f] mb-3">
          <div className="h-px w-6 bg-[#c5a26f]" /> PUBLIC INSTRUMENT
        </div>
        <h1 className="text-[#f1f3f8] leading-none mb-3">
          Treat hesitation<br />as information.
        </h1>
        <p className="max-w-[42ch] text-xl text-[#a3a9bc]">
          After events, embodied signals disappear. Errata is an observatory that lets organizers 
          recover the social hesitation that shaped what did — or did not — happen.
        </p>
      </div>

      <div className="mt-9 grid md:grid-cols-5 gap-6">
        {/* Create form — the primary action */}
        <div className="md:col-span-3 card p-8">
          <div className="section-title mb-5">CREATE AN OBSERVATORY</div>

          <form onSubmit={createObservatory} className="space-y-5">
            <div>
              <div className="label mb-1.5">Event title</div>
              <input 
                className="input" 
                placeholder="Q3 Strategy Offsite — Product + Growth" 
                value={form.title} 
                onChange={e => setForm({ ...form, title: e.target.value })} 
                required 
              />
            </div>

            <div>
              <div className="label mb-1.5">Short context (optional but recommended)</div>
              <textarea 
                className="input" 
                rows={3}
                placeholder="Two-day hybrid offsite. 19 people. Prioritization framework and headcount decisions."
                value={form.context} 
                onChange={e => setForm({ ...form, context: e.target.value })} 
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="label mb-1.5">Close submissions after</div>
                <select className="input" value={form.closesInDays} onChange={e => setForm({ ...form, closesInDays: e.target.value })}>
                  <option value="7">7 days</option>
                  <option value="14">14 days (recommended)</option>
                  <option value="30">30 days</option>
                  <option value="0">Do not auto-close</option>
                </select>
              </div>
              <div>
                <div className="label mb-1.5">Minimum observations to publish</div>
                <select className="input" value={form.minObs} onChange={e => setForm({ ...form, minObs: e.target.value })}>
                  <option value="2">2 (very small groups)</option>
                  <option value="3">3 (default)</option>
                  <option value="5">5</option>
                  <option value="8">8</option>
                </select>
              </div>
            </div>

            <label className="flex items-center gap-3 text-sm cursor-pointer pt-1">
              <input 
                type="checkbox" 
                checked={form.allowPublic} 
                onChange={e => setForm({ ...form, allowPublic: e.target.checked })} 
                className="accent-[#c5a26f]" 
              />
              <span>Allow this observatory to be published publicly (as a redacted learning error profile) once threshold is met</span>
            </label>

            {/* Turnstile container — required for creation */}
            <div>
              <div className="label mb-2">Confirm you are human (protects against spam)</div>
              <div ref={setTsContainer} className="min-h-[78px] flex items-center" />
              <div className="text-[11px] text-[#5a5f75] mt-1">We use Cloudflare Turnstile. No tracking profiles are created.</div>
            </div>

            <button 
              type="submit" 
              disabled={creating || !form.title.trim()} 
              className="btn btn-primary w-full mt-2 py-3 text-base disabled:opacity-60"
            >
              {creating ? 'Creating observatory…' : 'Create Observatory'}
            </button>
            <p className="text-[12px] text-[#5a5f75] text-center pt-1">
              You will receive a one-time admin key. Store it safely — it is the only way to manage or publish this observatory.
            </p>
          </form>
        </div>

        {/* Why this exists + how to use */}
        <div className="md:col-span-2 space-y-6">
          <div className="panel p-6 text-sm leading-relaxed">
            <div className="section-title mb-3">THE PROBLEM IT ADDRESSES</div>
            <p className="mb-3">
              Organizers lose the embodied information that used to be visible in a room: who went quiet, 
              whose shoulders dropped, who avoided eye contact when a decision was proposed. 
              Digital and hybrid formats erase most of these cues.
            </p>
            <p>
              Errata treats the resulting <span className="text-[#c5a26f]">hesitation</span> as data — 
              specifically data about status threats that distort perception, choice, and coordination.
            </p>
          </div>

          <div className="panel p-6 text-sm">
            <div className="section-title mb-3">WHAT YOU CAPTURE (ETHICALLY)</div>
            <ul className="space-y-2 text-[#b8bdcc]">
              <li className="flex gap-2"><span className="text-[#c5a26f] mt-1">•</span> Uncertainty label (how sure the observer is)</li>
              <li className="flex gap-2"><span className="text-[#c5a26f] mt-1">•</span> Known / Unknown split (what stayed legible vs opaque)</li>
              <li className="flex gap-2"><span className="text-[#c5a26f] mt-1">•</span> Evidence need (what would have surfaced signal)</li>
              <li className="flex gap-2"><span className="text-[#c5a26f] mt-1">•</span> Next question (the actual useful question for the group)</li>
            </ul>
          </div>

          {myObservatories.length > 0 && (
            <div className="panel p-5">
              <div className="text-xs text-[#c5a26f] tracking-widest mb-2">YOUR OBSERVATORIES (ADMIN)</div>
              <div className="space-y-1.5">
                {myObservatories.map(slug => (
                  <Link key={slug} to={`/e/${slug}`} className="block text-sm hover:text-[#f1f3f8] text-[#c3c7d3] font-medium">
                    {slug} <span className="text-[#5a5f75]">→</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* One-time admin key modal — critical UX */}
      <AnimatePresence>
        {adminKeyModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-6 z-[100]">
            <motion.div 
              initial={{ opacity: 0, y: 12, scale: 0.985 }} 
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 6 }}
              className="card max-w-lg w-full p-8"
            >
              <div className="flex items-start gap-4">
                <Lock className="w-9 h-9 text-[#c5a26f] mt-0.5" />
                <div className="flex-1">
                  <div className="text-xl font-semibold text-[#f1f3f8]">Save this admin key immediately</div>
                  <div className="text-sm mt-1 text-[#a3a9bc]">
                    This key was shown only once. It lets you close the observatory, delete observations, and publish the learning error profile. 
                    It is stored only in <span className="font-medium">your browser</span>.
                  </div>

                  <div className="mt-5 p-4 bg-[#0a0b12] rounded-xl font-mono text-sm break-all border border-[#353a52] text-[#f1f3f8]">
                    {adminKeyModal.key}
                  </div>

                  <div className="flex gap-3 mt-4">
                    <button 
                      onClick={() => {
                        navigator.clipboard.writeText(adminKeyModal.key)
                        toast.success('Copied. Store it somewhere durable (password manager, printed note).')
                      }}
                      className="btn flex-1"
                    >
                      <Copy className="w-4 h-4" /> Copy key
                    </button>
                    <button 
                      onClick={() => {
                        setAdminKeyModal(null)
                        navigate(`/e/${adminKeyModal.slug}`)
                      }} 
                      className="btn btn-primary flex-1"
                    >
                      I have saved it — continue
                    </button>
                  </div>
                  <button onClick={() => setAdminKeyModal(null)} className="text-xs text-[#5a5f75] mt-4 block mx-auto">I understand the risk of losing it</button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ---------- The Event Page: Submit + The Learning Error Profile ----------

function EventPage({ 
  isAdminFor, 
  setIsAdminFor 
}: { 
  isAdminFor: Record<string, boolean>
  setIsAdminFor: (v: Record<string, boolean>) => void 
}) {
  const { slug } = useParams<{ slug: string }>()

  const [profile, setProfile] = useState<Profile | null>(null)
  const [meta, setMeta] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const [adminKeyInput, setAdminKeyInput] = useState('')
  const [refreshing, setRefreshing] = useState(false)

  // Submission form state — deliberately structured around the four ethical fields
  const [form, setForm] = useState<ObservationInput>({
    momentText: '',
    statusThreat: 'expertise',
    impacts: { perception: false, choice: false, coordination: false },
    uncertainty: 'plausible',
    knownText: '',
    unknownText: '',
    evidenceNeed: '',
    nextQuestion: '',
  })
  const [tsContainer, setTsContainer] = useState<HTMLDivElement | null>(null)

  async function loadEverything() {
    if (!slug) return
    setLoading(true)
    try {
      const [m, p] = await Promise.all([
        api<any>(`/events/${slug}`),
        api<Profile>(`/profile/${slug}`),
      ])
      setMeta(m)
      setProfile(p)
      setIsAdmin(!!getAdminKey(slug))
    } catch (e: any) {
      toast.error(e.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadEverything() }, [slug])

  async function submitObservation(e: React.FormEvent) {
    e.preventDefault()
    if (!slug) return

    // Basic client validation for the four required signals
    if (form.momentText.trim().length < 18) return toast.error('Describe the moment more specifically.')
    if (!form.knownText.trim() && !form.unknownText.trim()) {
      return toast.error('Please fill the known/unknown split or the evidence need.')
    }
    if (!form.nextQuestion.trim()) return toast.error('The "next question" is required — it is often the most valuable output.')

    setSubmitting(true)
    try {
      if (!tsContainer) throw new Error('Verification not ready')
      const token = await getTurnstileToken(tsContainer, '1x00000000000000000000AA')

      await api('/observations', {
        method: 'POST',
        body: JSON.stringify({
          slug,
          turnstileToken: token,
          momentText: form.momentText,
          statusThreat: form.statusThreat,
          impacts: form.impacts,
          uncertainty: form.uncertainty,
          knownText: form.knownText,
          unknownText: form.unknownText,
          evidenceNeed: form.evidenceNeed,
          nextQuestion: form.nextQuestion,
        }),
      })

      toast.success('Observation recorded. The profile has been updated.')
      
      // Reset form but keep some momentum
      setForm({
        momentText: '', statusThreat: form.statusThreat, impacts: { perception:false, choice:false, coordination:false },
        uncertainty: 'plausible', knownText: '', unknownText: '', evidenceNeed: '', nextQuestion: '',
      })

      // Refresh profile
      await loadEverything()
    } catch (err: any) {
      toast.error(err.message || 'Submission failed')
    } finally {
      setSubmitting(false)
    }
  }

  async function verifyAsAdmin() {
    if (!slug || !adminKeyInput) return
    try {
      await api('/admin/verify', { method: 'POST', body: JSON.stringify({ slug, adminKey: adminKeyInput }) })
      saveAdminKey(slug, adminKeyInput)
      setIsAdmin(true)
      setIsAdminFor({ ...isAdminFor, [slug]: true })
      toast.success('Admin access granted. You can now manage and publish.')
    } catch (e: any) {
      toast.error(e.message || 'Invalid admin key')
    }
  }

  async function refreshProfile() {
    setRefreshing(true)
    await loadEverything()
    setRefreshing(false)
  }

  function exportMarkdown() {
    if (!profile) return
    const lines: string[] = []
    lines.push(`# ${profile.event.title}`)
    if (profile.event.context) lines.push(`\n${profile.event.context}\n`)
    lines.push(`\n**Observations:** ${profile.observations.length}`)
    lines.push(`**Generated:** ${new Date().toISOString()}\n`)

    lines.push('## Aperture Constellation (summary)\n')
    const byThreat: Record<string, number> = {}
    profile.observations.forEach(o => { byThreat[o.threat] = (byThreat[o.threat]||0)+1 })
    Object.entries(byThreat).forEach(([t, n]) => lines.push(`- ${THREAT_LABELS[t as StatusThreat]}: ${n}`))

    lines.push('\n## Open Questions\n')
    profile.observations.filter(o => o.nextQuestion).forEach(o => {
      lines.push(`- ${o.nextQuestion}  _(uncertainty: ${o.uncertainty})_`)
    })

    lines.push('\n## Evidence Needs\n')
    profile.observations.filter(o => o.evidence).forEach(o => lines.push(`- ${o.evidence}`))

    lines.push('\n## Selected Observations (anonymized)\n')
    profile.observations.slice(0, 8).forEach((o, i) => {
      lines.push(`### ${i+1}. ${THREAT_LABELS[o.threat]} — ${o.uncertainty}`)
      lines.push(o.moment + '\n')
      if (o.known) lines.push(`**Known:** ${o.known}`)
      if (o.unknown) lines.push(`**Unknown:** ${o.unknown}`)
      if (o.evidence) lines.push(`**Evidence need:** ${o.evidence}`)
      if (o.nextQuestion) lines.push(`**Next question:** ${o.nextQuestion}`)
      lines.push('')
    })

    const md = lines.join('\n')
    navigator.clipboard.writeText(md)
    toast.success('Markdown copied. Paste into Notion, Obsidian, or a doc.')
  }

  function printProfile() {
    window.print()
  }

  async function closeEvent() {
    if (!slug || !confirm('Close this observatory to new submissions?')) return
    const key = getAdminKey(slug)
    try {
      await fetch(`${API}/admin/close`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Key': key || '' },
        body: JSON.stringify({ slug }),
      })
      toast.success('Observatory closed')
      loadEverything()
    } catch (e: any) { toast.error(e.message) }
  }

  async function publishPublic() {
    if (!slug) return
    const key = getAdminKey(slug)
    try {
      await fetch(`${API}/admin/publish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Key': key || '' },
        body: JSON.stringify({ slug }),
      })
      toast.success('Published to the public gallery')
      loadEverything()
    } catch (e: any) { toast.error(e.message) }
  }

  async function deleteObs(id: string) {
    if (!slug || !confirm('Delete this observation?')) return
    const key = getAdminKey(slug)
    try {
      await fetch(`${API}/admin/observations/${id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Key': key || '' },
        body: JSON.stringify({ slug }),
      })
      toast.success('Observation removed')
      loadEverything()
    } catch (e: any) { toast.error(e.message) }
  }

  if (loading) {
    return <div className="container py-20 text-center text-sm">Loading observatory…</div>
  }

  if (!profile || !meta) {
    return (
      <div className="container py-16">
        <div className="max-w-md mx-auto text-center">
          <AlertCircle className="mx-auto mb-4 text-[#c5a26f]" />
          <div className="text-xl mb-2">Observatory not found</div>
          <Link to="/" className="btn">Create one or go home</Link>
        </div>
      </div>
    )
  }

  const event = profile.event
  const obs = profile.observations
  const isClosed = meta.event?.isClosed || (event.closesAt && Date.now() > event.closesAt)

  return (
    <div className="container py-8">
      {/* Event header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3">
            <div className="status-pill text-[#c5a26f] border-[#c5a26f]/40">{obs.length} observations</div>
            {event.publishedAt && <div className="status-pill">PUBLISHED</div>}
            {isClosed && <div className="status-pill text-[#c97c7c]">CLOSED</div>}
          </div>
          <h1 className="text-[#f1f3f8] mt-1.5 leading-none">{event.title}</h1>
          {event.context && <p className="mt-2 max-w-2xl text-[#a3a9bc]">{event.context}</p>}
        </div>
        <div className="flex gap-2 no-print">
          <button onClick={refreshProfile} className="btn flex items-center gap-2" disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <button onClick={exportMarkdown} className="btn flex items-center gap-2"><Download className="w-4 h-4" /> Export MD</button>
          <button onClick={printProfile} className="btn flex items-center gap-2"><Download className="w-4 h-4" /> Print / PDF</button>
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* The Learning Error Profile — the actual artifact */}
        <div className="lg:col-span-3 space-y-6">
          <ApertureConstellation observations={obs} />

          {/* Known / Unknown ledger */}
          <div className="card p-6">
            <div className="section-title mb-4">KNOWN / UNKNOWN SPLIT</div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#7f8496] mb-2">What remained legible</div>
                <div className="space-y-3 text-sm">
                  {obs.filter(o => o.known).length ? obs.filter(o => o.known).map((o,i) => (
                    <div key={i} className="panel p-3 text-[#c3c7d3]">{o.known}</div>
                  )) : <div className="text-[#5a5f75] text-sm">No knowns recorded yet.</div>}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-[#7f8496] mb-2">What stayed opaque or distorted</div>
                <div className="space-y-3 text-sm">
                  {obs.filter(o => o.unknown).length ? obs.filter(o => o.unknown).map((o,i) => (
                    <div key={i} className="panel p-3 text-[#c3c7d3]">{o.unknown}</div>
                  )) : <div className="text-[#5a5f75] text-sm">No unknowns recorded yet.</div>}
                </div>
              </div>
            </div>
          </div>

          {/* Evidence gaps + next questions — the coordination gold */}
          <div className="card p-6">
            <div className="section-title mb-4">EVIDENCE NEEDS &amp; OPEN QUESTIONS</div>
            <div className="space-y-5 text-sm">
              {obs.filter(o => o.evidence || o.nextQuestion).length === 0 && (
                <div className="text-[#5a5f75]">Nothing recorded yet. The first contributions will appear here.</div>
              )}
              {obs.filter(o => o.evidence).map((o, idx) => (
                <div key={idx} className="pl-4 border-l border-[#c5a26f]/40">
                  <div className="text-[#c5a26f] text-xs mb-px">EVIDENCE THAT WOULD HAVE HELPED</div>
                  <div>{o.evidence}</div>
                </div>
              ))}
              {obs.filter(o => o.nextQuestion).map((o, idx) => (
                <div key={idx} className="pl-4 border-l border-[#8fa3c2]/40">
                  <div className="text-[#8fa3c2] text-xs mb-px">NEXT QUESTION FOR THE GROUP</div>
                  <div className="text-[#e2e5f0]">{o.nextQuestion} <span className="text-[#5a5f75] text-xs">({o.uncertainty})</span></div>
                </div>
              ))}
            </div>
          </div>

          {/* Exemplar observations (quiet, no names, no performance) */}
          <div>
            <div className="flex items-baseline justify-between mb-3 px-1">
              <div className="section-title">EXEMPLAR OBSERVATIONS</div>
              <div className="text-xs text-[#5a5f75]">anonymized • ordered by recency</div>
            </div>
            <div className="space-y-3">
              {obs.length === 0 && (
                <div className="panel p-8 text-center text-sm">No observations yet. The form on the right is how they appear.</div>
              )}
              {obs.slice(0, 9).map((o, idx) => (
                <div key={idx} className="obs-card card p-5 text-sm border-l-2" style={{ borderLeftColor: o.uncertainty === 'evident' ? '#c5a26f' : '#353a52' }}>
                  <div className="flex gap-2 flex-wrap mb-2">
                    <span className="threat-badge">{THREAT_LABELS[o.threat]}</span>
                    <span className={`status-pill uncertainty-${o.uncertainty}`}>{UNCERTAINTY_LABELS[o.uncertainty]}</span>
                    {o.impacts.perception && <span className="status-pill">perception</span>}
                    {o.impacts.choice && <span className="status-pill">choice</span>}
                    {o.impacts.coordination && <span className="status-pill">coordination</span>}
                  </div>
                  <div className="leading-snug text-[#d4d8e3]">{o.moment}</div>

                  {(o.known || o.unknown) && (
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 text-xs text-[#9aa0b5]">
                      {o.known && <div><span className="text-[#5a5f75]">Known:</span> {o.known}</div>}
                      {o.unknown && <div><span className="text-[#5a5f75]">Unknown:</span> {o.unknown}</div>}
                    </div>
                  )}
                  {o.evidence && <div className="mt-2 text-xs text-[#8fa3c2]">Evidence need: {o.evidence}</div>}
                  {o.nextQuestion && <div className="mt-1 text-xs text-[#c5a26f]">Next question: {o.nextQuestion}</div>}

                  {isAdmin && (
                    <button onClick={() => deleteObs(o.id)} className="mt-3 text-xs flex items-center gap-1 text-[#c97c7c] hover:underline">
                      <Trash2 className="w-3.5 h-3.5" /> remove
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Contribution form + Admin panel */}
        <div className="lg:col-span-2 space-y-6">
          {/* Contribute */}
          {!isClosed && (
            <div className="card p-6">
              <div className="section-title mb-1">CONTRIBUTE AN OBSERVATION</div>
              <div className="text-xs text-[#7f8496] mb-5">Anonymous. About the room, not you. Focus on what could not be sensed.</div>

              <form onSubmit={submitObservation} className="space-y-5 text-sm">
                <div className="form-step">
                  <div className="label mb-1.5">The moment of hesitation or lost signal</div>
                  <textarea 
                    value={form.momentText} 
                    onChange={e => setForm({ ...form, momentText: e.target.value })} 
                    placeholder="When the prioritization matrix was presented, the three people who had raised concerns earlier stayed completely silent for the entire discussion. The room concluded we had alignment." 
                    required 
                  />
                  <div className="text-[11px] text-[#5a5f75] mt-1">Be specific about what was visible (or not) in the room or on the call.</div>
                </div>

                <div className="form-step">
                  <div className="label mb-2">Primary status threat or signal that was missing</div>
                  <div className="grid grid-cols-1 gap-1.5">
                    {(Object.keys(THREAT_LABELS) as StatusThreat[]).map(th => (
                      <label key={th} className={`flex items-center gap-3 px-3 py-2 rounded-lg border cursor-pointer ${form.statusThreat === th ? 'border-[#c5a26f] bg-[#1a1c28]' : 'border-[#25283a] hover:border-[#353a52]'}`}>
                        <input 
                          type="radio" 
                          name="threat" 
                          checked={form.statusThreat === th} 
                          onChange={() => setForm({ ...form, statusThreat: th })} 
                        />
                        <span>{THREAT_LABELS[th]}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="form-step">
                  <div className="label mb-2">What this distorted (can be more than one)</div>
                  <div className="flex flex-wrap gap-2">
                    {(['perception','choice','coordination'] as const).map(k => (
                      <label key={k} className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#25283a] cursor-pointer has-[:checked]:border-[#c5a26f] has-[:checked]:bg-[#1a1c28]">
                        <input 
                          type="checkbox" 
                          checked={form.impacts[k]} 
                          onChange={e => setForm({ ...form, impacts: { ...form.impacts, [k]: e.target.checked } })} 
                        />
                        <span className="capitalize text-sm">{k}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="form-step">
                  <div className="label mb-2">How certain are you this was actually hesitation / signal loss?</div>
                  <div className="flex gap-2">
                    {(['speculative','plausible','evident'] as Uncertainty[]).map(u => (
                      <button 
                        type="button" 
                        key={u}
                        onClick={() => setForm({ ...form, uncertainty: u })}
                        className={`flex-1 py-2 rounded-xl border text-sm transition ${form.uncertainty === u ? 'border-[#c5a26f] text-[#f1f3f8] bg-[#1a1c28]' : 'border-[#25283a] hover:border-[#3a3f55]'}`}
                      >
                        {UNCERTAINTY_LABELS[u]}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-step">
                  <div className="label mb-1.5">Known / Unknown split (what the group could still register vs what stayed hidden)</div>
                  <div className="grid grid-cols-1 gap-3">
                    <textarea value={form.knownText} onChange={e=>setForm({...form,knownText:e.target.value})} placeholder="What we still knew or could perceive..." />
                    <textarea value={form.unknownText} onChange={e=>setForm({...form,unknownText:e.target.value})} placeholder="What remained ambiguous, distorted, or invisible..." />
                  </div>
                </div>

                <div className="form-step">
                  <div className="label mb-1.5">Evidence need — one concrete thing that would have given the room better signal</div>
                  <textarea value={form.evidenceNeed} onChange={e=>setForm({...form,evidenceNeed:e.target.value})} placeholder="A private 60-second temperature poll visible only in aggregate right after the proposal..." />
                </div>

                <div className="form-step">
                  <div className="label mb-1.5">The single next question this raises for the organizers or the group</div>
                  <textarea value={form.nextQuestion} onChange={e=>setForm({...form,nextQuestion:e.target.value})} placeholder="Which of the earlier concerns are still live, and do they change the weighting we just assigned?" required />
                </div>

                <div>
                  <div className="label mb-2">Human verification</div>
                  <div ref={setTsContainer} className="min-h-[72px]" />
                </div>

                <button 
                  type="submit" 
                  disabled={submitting} 
                  className="btn btn-primary w-full py-3 mt-1 disabled:opacity-60"
                >
                  {submitting ? 'Recording observation…' : 'Record Anonymous Observation'}
                </button>
                <div className="text-[11px] text-center text-[#5a5f75]">Protected by Turnstile. No identity is stored. Rate limited per event.</div>
              </form>
            </div>
          )}

          {isClosed && <div className="panel p-5 text-sm">This observatory is closed to new observations.</div>}

          {/* Admin zone */}
          <div className="panel p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="section-title">ORGANIZER CONTROLS</div>
              {isAdmin && <div className="text-xs text-[#c5a26f]">AUTHENTICATED</div>}
            </div>

            {!isAdmin ? (
              <div className="text-sm space-y-3">
                <p className="text-[#7f8496]">If you created this observatory you have a one-time admin key. Paste it here to manage, close, delete observations, or publish the public learning error profile.</p>
                <div className="flex gap-2">
                  <input 
                    className="input font-mono text-xs" 
                    placeholder="adm_xxxxxxxxxxxxxxxx" 
                    value={adminKeyInput} 
                    onChange={e => setAdminKeyInput(e.target.value)} 
                  />
                  <button onClick={verifyAsAdmin} className="btn">Unlock</button>
                </div>
                <div className="text-xs text-[#5a5f75]">The key lives only in your browser. Losing it means losing admin rights.</div>
              </div>
            ) : (
              <div className="space-y-3 text-sm">
                <div className="flex flex-wrap gap-2">
                  <button onClick={closeEvent} className="btn btn-danger flex-1">Close to new submissions</button>
                  <button onClick={publishPublic} className="btn flex-1">Publish to public gallery</button>
                </div>
                <button 
                  onClick={() => { 
                    if (slug) { removeAdminKey(slug); setIsAdmin(false); setIsAdminFor({ ...isAdminFor, [slug]: false }) }
                  }} 
                  className="text-xs text-[#c97c7c] flex items-center gap-1"
                >
                  <X className="w-3.5 h-3.5" /> Forget admin key from this browser
                </button>
                <div className="text-[11px] text-[#5a5f75] pt-1">
                  Publishing requires meeting the minimum observation threshold set at creation.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ---------- Public Gallery of Published Errata ----------

function PublicGallery() {
  const [list, setList] = useState<PublicErratum[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api<{ errata: PublicErratum[] }>('/public/errata')
      .then(r => setList(r.errata))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  return (
    <div className="container py-10">
      <div className="max-w-2xl">
        <div className="section-title">PUBLISHED ERRATA</div>
        <h2 className="text-[#f1f3f8] mt-1">Learning error profiles made public by organizers</h2>
        <p className="mt-2 text-[#a3a9bc]">These contain no individual attribution. They exist so that patterns across groups become visible over time. No comments, no likes, no performance.</p>
      </div>

      {loading && <div className="mt-10 text-sm">Loading published observatories…</div>}

      {!loading && list.length === 0 && (
        <div className="mt-10 panel p-8 text-sm max-w-md">No errata have been published yet. The first ones will appear here when organizers choose to share them.</div>
      )}

      <div className="mt-8 grid md:grid-cols-2 gap-4">
        {list.map((e, i) => (
          <Link key={i} to={`/e/${e.slug}`} className="card p-6 group hover:border-[#353a52]">
            <div className="text-sm text-[#c5a26f] mb-1">{e.obsCount} observations • published {new Date(e.publishedAt).toLocaleDateString()}</div>
            <div className="font-semibold text-lg text-[#f1f3f8] group-hover:underline">{e.title}</div>
            {e.context && <div className="mt-2 text-sm text-[#a3a9bc] line-clamp-2">{e.context}</div>}
            <div className="mt-4 text-xs text-[#7f8496] flex items-center gap-1">View the learning error profile <ArrowRight className="w-3.5" /></div>
          </Link>
        ))}
      </div>
    </div>
  )
}

function NotFound() {
  return (
    <div className="container py-20 text-center">
      <div className="text-6xl mb-4 opacity-40">∅</div>
      <div className="text-xl">Nothing here.</div>
      <Link to="/" className="btn mt-6 inline-flex">Return to the observatory</Link>
    </div>
  )
}
